/**
 * The subagent side of the turn fold (HIG-5971): events addressed to a
 * subagent stream (`parentToolCallId` + `subagentIndex`) fold into the parent
 * tool call's `subagents` instead of the transcript. Same contract as
 * `applyEvent` — pure, immutable `TurnState`, patches out.
 */
import type { SubagentState, ToolCall } from '../types'
import type { StorePatch, TurnState } from './accumulator'
import type { ChatSSEContentEvent, ChatSSELoadingEvent, ChatSSEToolResponseEvent, ChatSSEToolUseEvent } from './events'
import { parseToolInput, toolCallExtras } from './fold-utils'

/**
 * A subagent's child-tool event. These patch the parent *message* (child tool
 * calls live on it, keyed by `metadata.parentToolId`), so unlike text and
 * reasoning they can't be folded into `subagents` — they need the parent's
 * message id, which the `loading` registration supplies.
 */
export type SubagentToolEvent = (ChatSSELoadingEvent | ChatSSEToolUseEvent | ChatSSEToolResponseEvent) & {
  parentToolCallId: string
  subagentIndex: number
}

/** Mark every subagent under `parentToolCallId` completed, e.g. when the parent tool call responds. */
export function finalizeSubagentsForParent(state: TurnState, parentToolCallId: string): TurnState {
  const finalized: Record<string, SubagentState> = { ...state.subagents }
  for (const [key, sub] of Object.entries(state.subagents)) {
    if (sub.parentToolCallId === parentToolCallId) {
      finalized[key] = { ...sub, status: 'completed', isThinkingStreaming: false }
    }
  }
  return { ...state, subagents: finalized }
}

/** Patch flushing a parent tool call's current subagent states, if it has any. */
export function subagentFlushPatch(state: TurnState, parentToolCallId: string): StorePatch | null {
  const messageId = state.subagentParents[parentToolCallId]
  if (!messageId) return null
  const subagents = Object.values(state.subagents)
    .filter((sub) => sub.parentToolCallId === parentToolCallId)
    .sort((a, b) => a.index - b.index)
    .map((sub) => ({ ...sub }))
  if (subagents.length === 0) return null
  return { op: 'updateToolCall', chatId: state.chatId, messageId, toolCallId: parentToolCallId, updates: { subagents } }
}

/** The store patch one child-tool event makes against a known parent message. */
function subagentToolPatch(chatId: string, messageId: string, event: SubagentToolEvent): StorePatch | null {
  switch (event.type) {
    case 'loading': {
      if (!event.toolId) return null
      return {
        op: 'updateToolCall',
        chatId,
        messageId,
        toolCallId: event.toolId,
        updates: {
          status: event.loaded === true ? 'success' : 'executing',
          ...(event.view ? { view: event.view } : {}),
        },
      }
    }
    case 'toolUse': {
      const toolCall: ToolCall = {
        id: event.toolId,
        toolName: event.name,
        toolInput: parseToolInput(event.input),
        toolType: 'builtin',
        status: 'executing',
        metadata: { parentToolId: event.parentToolCallId },
        ...(event.view ? { view: event.view } : {}),
      }
      return { op: 'addToolCall', chatId, messageId, toolCall }
    }
    case 'toolResponse':
      return {
        op: 'updateToolCall',
        chatId,
        messageId,
        toolCallId: event.toolId,
        updates: { status: 'success', result: event.content, ...toolCallExtras(event) },
      }
  }
}

/** Hold a child-tool event until the parent registration reveals its message id. */
function queueToolEvent(state: TurnState, event: SubagentToolEvent): TurnState {
  const pending = state.pendingSubagentTools[event.parentToolCallId] ?? []
  return {
    ...state,
    pendingSubagentTools: { ...state.pendingSubagentTools, [event.parentToolCallId]: [...pending, event] },
  }
}

/**
 * Everything a just-registered parent owes the store: the child-tool events
 * that arrived before the registration, replayed in arrival order, then the
 * subagent states folded so far. Both can predate the `loading` event that
 * names the parent message — dropping either loses tools or streamed text.
 */
export function drainSubagentPatches(
  state: TurnState,
  parentToolCallId: string,
): [TurnState, ReadonlyArray<StorePatch>] {
  const messageId = state.subagentParents[parentToolCallId]
  if (!messageId) return [state, []]

  const pending = state.pendingSubagentTools[parentToolCallId] ?? []
  const patches: Array<StorePatch> = []
  for (const event of pending) {
    const patch = subagentToolPatch(state.chatId, messageId, event)
    if (patch) patches.push(patch)
  }
  const flush = subagentFlushPatch(state, parentToolCallId)
  if (flush) patches.push(flush)

  if (pending.length === 0) return [state, patches]
  const remaining: Record<string, ReadonlyArray<SubagentToolEvent>> = { ...state.pendingSubagentTools }
  delete remaining[parentToolCallId]
  return [{ ...state, pendingSubagentTools: remaining }, patches]
}

/** Fold an event addressed to a subagent stream into the parent tool call. */
export function applySubagentEvent(
  state: TurnState,
  event: ChatSSEContentEvent & { parentToolCallId: string; subagentIndex: number },
): [TurnState, ReadonlyArray<StorePatch>] {
  const { parentToolCallId, subagentIndex: index } = event
  const key = `${parentToolCallId}:${index}`
  const existing: SubagentState = state.subagents[key] ?? {
    index,
    messageId: '',
    parentToolCallId,
    content: '',
    thinkingContent: '',
    isThinkingStreaming: false,
    status: 'streaming',
  }
  const withSub = (sub: SubagentState): TurnState => ({
    ...state,
    subagents: { ...state.subagents, [key]: sub },
  })
  const parentMessageId = state.subagentParents[parentToolCallId]

  switch (event.type) {
    case 'metadata': {
      const messageId = typeof event.messageId === 'string' ? event.messageId : existing.messageId
      return [withSub({ ...existing, messageId }), []]
    }
    case 'reasoning': {
      if (!event.content) return [withSub(existing), []]
      const next = withSub({
        ...existing,
        thinkingContent: existing.thinkingContent + event.content,
        isThinkingStreaming: true,
        messageId: event.messageId || existing.messageId,
      })
      const flush = subagentFlushPatch(next, parentToolCallId)
      return [next, flush ? [flush] : []]
    }
    case 'text': {
      if (!event.content) return [withSub(existing), []]
      const next = withSub({
        ...existing,
        content: existing.content + event.content,
        isThinkingStreaming: false,
        messageId: event.messageId || existing.messageId,
      })
      const flush = subagentFlushPatch(next, parentToolCallId)
      return [next, flush ? [flush] : []]
    }
    case 'loading':
    case 'toolUse':
    case 'toolResponse': {
      const next = withSub(existing)
      const toolEvent: SubagentToolEvent = event
      if (event.type === 'loading' && !event.toolId) return [next, []]
      // No parent message id yet — queue rather than drop; the `loading`
      // registration replays these (child tools used to vanish when the
      // subagent's stream opened before its registration landed).
      if (!parentMessageId) return [queueToolEvent(next, toolEvent), []]
      const patch = subagentToolPatch(next.chatId, parentMessageId, toolEvent)
      return [next, patch ? [patch] : []]
    }
    // usage etc. are telemetry-only for subagents — track the fold, emit nothing.
    default:
      return [withSub(existing), []]
  }
}
